/**
 * Battery (akü), lift (direk) and eco-driving (ADR 0034): summary items, charts and file sheets, shared by the
 * screens and their exports so both show the same numbers. Measured data only; a vehicle without a battery
 * source is labelled, never given a voltage.
 */
import { localParts, type BatteryResult, type EcoResult, type LiftResult, type LiftRow, type EcoRow } from "@fts/core";
import type { TFn } from "../../i18n";
import type { ChartSpec } from "../../lib/chart";
import { systemText, type Sheet } from "../../lib/export/sheet";
import { formatInt, formatNum, trendOf } from "../../lib/report-figures";
import { drivingRowLabel, durationLabel } from "../../lib/report-sheets";
import type { Ctx } from "./presenters";
import type { SummaryItem } from "./ReportParts";

/* ---------------- battery ---------------- */

/** The opportunity-charging limit in minutes (the label of both session kinds). */
export const oppMin = (r: Pick<BatteryResult, "rules">) => Math.round(r.rules.opportunityMaxS / 60);

export function batterySummary(ctx: Ctx, r: BatteryResult, p: BatteryResult | null): SummaryItem[] {
  const { t, locale } = ctx;
  const a = r.totals;
  const b = p?.totals ?? null;
  return [
    { label: t("battery.sum.measured"), value: t("battery.sum.measuredValue", { measured: a.measured, total: a.vehicles }) },
    { label: t("battery.sum.charges"), value: formatInt(a.charges, locale), trend: b && trendOf(a.charges, b.charges), previous: b && formatInt(b.charges, locale) },
    { label: t("battery.sum.chargeTime"), value: durationLabel(t, a.chargeS), trend: b && trendOf(a.chargeS, b.chargeS), previous: b && durationLabel(t, b.chargeS) },
    { label: t("battery.sum.opportunity"), value: formatInt(a.opportunity, locale), trend: b && trendOf(a.opportunity, b.opportunity), previous: b && formatInt(b.opportunity, locale) },
    { label: t("battery.sum.lowAlarms"), value: formatInt(a.lowAlarms, locale), trend: b && trendOf(a.lowAlarms, b.lowAlarms), previous: b && formatInt(b.lowAlarms, locale) },
  ];
}

/** Charge time per vehicle (hours), opportunity charging stacked on top. Vehicles without a source are left out. */
export function batteryChart(ctx: Ctx, r: BatteryResult): ChartSpec {
  const { t, locale } = ctx;
  const vs = r.vehicles.filter((v) => v.source !== "none" && v.readings > 0);
  const bars = vs.map((v) => {
    const opp = v.sessions.filter((s) => s.opportunity).reduce((x, s) => x + s.durS, 0) / 3600;
    const full = v.sessions.filter((s) => !s.opportunity).reduce((x, s) => x + s.durS, 0) / 3600;
    return { key: v.vehicleId, label: v.fleetNo, values: [Math.round(full * 10) / 10, Math.round(opp * 10) / 10] };
  });
  const top = [...bars].sort((x, y) => y.values[0]! + y.values[1]! - (x.values[0]! + x.values[1]!))[0];
  return {
    orientation: "horizontal",
    series: [
      { label: t("battery.kind.full", { min: oppMin(r) }), color: "ramp3" },
      { label: t("battery.kind.opportunity", { min: oppMin(r) }), color: "ramp1" },
    ],
    bars,
    digits: 1,
    unit: t("reports.unit.hours"),
    locale,
    categoryLabel: t("reports.col.vehicle"),
    totalLabel: t("battery.chart.total"),
    summary: top ? t("battery.chart.summary", { vehicle: top.label, h: formatNum(top.values[0]! + top.values[1]!, locale, 1) }) : "",
  };
}

/** Opportunity charges by local start hour (0-23). */
export function opportunityByHour(r: BatteryResult): number[] {
  const out = Array<number>(24).fill(0);
  for (const v of r.vehicles) for (const s of v.sessions) if (s.opportunity) out[Math.floor(localParts(new Date(s.start), r.tz).minutes / 60)]!++;
  return out;
}

export function opportunityChart(ctx: Ctx, r: BatteryResult): ChartSpec {
  const { t, locale } = ctx;
  const h = opportunityByHour(r);
  return {
    orientation: "vertical",
    series: [{ label: t("battery.kind.opportunity", { min: oppMin(r) }), color: "ramp1" }],
    bars: h.map((n, i) => ({ key: String(i), label: String(i).padStart(2, "0"), title: t("battery.opp.hourTitle", { h: String(i).padStart(2, "0") }), values: [n] })),
    digits: 0,
    locale,
    categoryLabel: t("battery.opp.hour"),
    summary: t("battery.opp.summary", { count: h.reduce((a, b) => a + b, 0) }),
  };
}

export function batterySheets(t: TFn, r: BatteryResult): Sheet[] {
  return [
    {
      name: t("battery.sheet.vehicles"),
      columns: [
        { header: t("reports.col.vehicle"), type: "text", width: 10 },
        { header: t("battery.col.source"), type: "text", width: 24 },
        { header: t("battery.col.readings"), type: "int" },
        { header: t("battery.col.min"), type: "dec2" },
        { header: t("battery.col.avg"), type: "dec2" },
        { header: t("battery.col.max"), type: "dec2" },
        { header: t("battery.col.lowV"), type: "dec1" },
        { header: t("battery.col.lowReadings"), type: "int" },
        { header: t("battery.col.lowAlarms"), type: "int" },
        { header: t("battery.col.charges"), type: "int" },
        { header: t("battery.col.opportunity"), type: "int" },
      ],
      rows: r.vehicles.map((v) => [
        v.fleetNo,
        systemText(v.source === "none" || v.readings === 0 ? t(v.source === "none" ? "battery.noSource" : "battery.noData") : t(`calib.source.${v.source}`)),
        v.readings, v.minV, v.avgV, v.maxV, v.lowV, v.lowReadings, v.lowAlarms, v.sessions.length, v.sessions.filter((s) => s.opportunity).length,
      ]),
    },
    {
      name: t("battery.sheet.sessions"),
      columns: [
        { header: t("reports.col.vehicle"), type: "text", width: 10 },
        { header: t("battery.col.start"), type: "datetime" },
        { header: t("battery.col.end"), type: "datetime" },
        { header: t("battery.col.duration"), type: "duration" },
        { header: t("battery.col.startV"), type: "dec2" },
        { header: t("battery.col.endV"), type: "dec2" },
        { header: t("battery.col.peakV"), type: "dec2" },
        { header: t("battery.col.kind"), type: "text" },
      ],
      rows: r.vehicles.flatMap((v) =>
        v.sessions.map((s) => [v.fleetNo, s.start, s.end, s.durS, s.startV, s.endV, s.peakV, systemText(t(s.opportunity ? "battery.kind.opportunity" : "battery.kind.full", { min: oppMin(r) }) + (s.open ? ` · ${t("battery.kind.open")}` : ""))]),
      ),
    },
  ];
}

/* ---------------- lift ---------------- */

export const liftRowLabel = (t: TFn, r: Pick<LiftRow, "kind" | "name" | "cards">) => drivingRowLabel(t, r);

export function liftSummary(ctx: Ctx, r: LiftResult, p: LiftResult | null): SummaryItem[] {
  const { t, locale } = ctx;
  const a = r.totals;
  const b = p?.totals ?? null;
  return [
    { label: t("lift.sum.lifts"), value: formatInt(a.lifts, locale), trend: b && trendOf(a.lifts, b.lifts), previous: b && formatInt(b.lifts, locale) },
    { label: t("lift.sum.liftTime"), value: durationLabel(t, a.liftS), trend: b && trendOf(a.liftS, b.liftS), previous: b && durationLabel(t, b.liftS) },
    { label: t("lift.sum.whileDriving"), value: formatInt(a.whileDriving, locale), trend: b && trendOf(a.whileDriving, b.whileDriving), previous: b && formatInt(b.whileDriving, locale) },
    { label: t("lift.sum.whileDrivingTime"), value: durationLabel(t, a.whileDrivingS), trend: b && trendOf(a.whileDrivingS, b.whileDrivingS), previous: b && durationLabel(t, b.whileDrivingS) },
  ];
}

export function liftChart(ctx: Ctx, r: LiftResult): ChartSpec {
  const { t, locale } = ctx;
  const rows = r.rows.filter((x) => !x.suppressed);
  const bars = rows.map((x) => ({ key: `${x.kind}:${x.id ?? ""}`, label: liftRowLabel(t, x), values: [x.lifts - x.whileDriving, x.whileDriving] }));
  return {
    orientation: "horizontal",
    series: [
      { label: t("lift.chart.standing"), color: "ramp2" },
      { label: t("lift.chart.driving"), color: "ramp4" },
    ],
    bars,
    digits: 0,
    locale,
    categoryLabel: r.group === "operator" ? t("reports.col.operator") : t("reports.col.vehicle"),
    totalLabel: t("lift.sum.lifts"),
    summary: t("lift.chart.summary", { lifts: formatInt(r.totals.lifts, locale), driving: formatInt(r.totals.whileDriving, locale) }),
  };
}

export function liftSheet(t: TFn, r: LiftResult): Sheet {
  const byOp = r.group === "operator";
  const cells = (m: { lifts: number; liftS: number; whileDriving: number; whileDrivingS: number }) => [m.lifts, m.liftS, m.whileDriving, m.whileDrivingS];
  return {
    name: t("lift.title"),
    columns: [
      { header: byOp ? t("reports.col.operator") : t("reports.col.vehicle"), type: "text", width: byOp ? 26 : 10 },
      { header: t("lift.col.lifts"), type: "int" },
      { header: t("lift.col.liftTime"), type: "duration" },
      { header: t("lift.col.whileDriving"), type: "int" },
      { header: t("lift.col.whileDrivingTime"), type: "duration" },
    ],
    rows: [
      ...r.rows.map((x) => [x.kind === "operator" || x.kind === "vehicle" ? (x.name ?? "—") : systemText(liftRowLabel(t, x)), ...(x.suppressed ? [null, null, null, null] : cells(x))]),
      ...(r.rows.length ? [[systemText(t("reports.totals")), ...cells(r.totals)]] : []),
    ],
  };
}

/* ---------------- eco driving ---------------- */

export function ecoSummary(ctx: Ctx, r: EcoResult, p: EcoResult | null): SummaryItem[] {
  const { t, locale } = ctx;
  const a = r.totals;
  const b = p?.totals ?? null;
  const item = (label: string, x: number, y: number | undefined) => ({ label, value: formatInt(x, locale), trend: b && trendOf(x, y ?? 0), previous: b && formatInt(y ?? 0, locale) });
  return [
    item(t("eco.sum.total"), a.total, b?.total),
    item(t("eco.type.harsh_accel"), a.accel, b?.accel),
    item(t("eco.type.harsh_brake"), a.brake, b?.brake),
    item(t("eco.type.harsh_corner"), a.corner, b?.corner),
  ];
}

export function ecoChart(ctx: Ctx, r: EcoResult): ChartSpec {
  const { t, locale } = ctx;
  const rows = r.rows.filter((x) => !x.suppressed);
  return {
    orientation: "horizontal",
    series: [
      { label: t("eco.type.harsh_accel"), color: "ramp1" },
      { label: t("eco.type.harsh_brake"), color: "ramp3" },
      { label: t("eco.type.harsh_corner"), color: "ramp4" },
    ],
    bars: rows.map((x) => ({ key: `${x.kind}:${x.id ?? ""}`, label: drivingRowLabel(t, x), values: [x.accel, x.brake, x.corner] })),
    digits: 0,
    locale,
    categoryLabel: r.group === "operator" ? t("reports.col.operator") : t("reports.col.vehicle"),
    totalLabel: t("eco.sum.total"),
    summary: t("eco.chart.summary", { total: formatInt(r.totals.total, locale) }),
  };
}

export function ecoSheets(t: TFn, r: EcoResult): Sheet[] {
  const byOp = r.group === "operator";
  const cells = (m: Pick<EcoRow, "accel" | "brake" | "corner" | "total" | "maxG">) => [m.accel, m.brake, m.corner, m.total, m.maxG];
  return [
    {
      name: t("eco.sheet.counts"),
      columns: [
        { header: byOp ? t("reports.col.operator") : t("reports.col.vehicle"), type: "text", width: byOp ? 26 : 10 },
        { header: t("eco.type.harsh_accel"), type: "int" },
        { header: t("eco.type.harsh_brake"), type: "int" },
        { header: t("eco.type.harsh_corner"), type: "int" },
        { header: t("eco.col.total"), type: "int" },
        { header: t("eco.col.maxG"), type: "dec2" },
      ],
      rows: [
        ...r.rows.map((x) => [x.kind === "operator" || x.kind === "vehicle" ? (x.name ?? "—") : systemText(drivingRowLabel(t, x)), ...(x.suppressed ? [null, null, null, null, null] : cells(x))]),
        ...(r.rows.length ? [[systemText(t("reports.totals")), ...cells(r.totals)]] : []),
      ],
    },
    {
      name: t("eco.sheet.events"),
      columns: [
        { header: t("eco.col.at"), type: "datetimeS" },
        { header: t("reports.col.vehicle"), type: "text", width: 10 },
        { header: t("eco.col.type"), type: "text", width: 18 },
        { header: t("eco.col.g"), type: "dec2" },
        { header: t("reports.col.operator"), type: "text", width: 24 },
        { header: t("eco.col.lat"), type: "coord" },
        { header: t("eco.col.lon"), type: "coord" },
      ],
      rows: r.events.map((e) => [e.at, e.fleetNo, systemText(t(`eco.type.${e.type}`)), e.g, e.operatorName, e.lat, e.lon]),
    },
  ];
}
